import axios from 'axios';
import { apiUrl } from '../Components/Pages/constants';

const baseUrl = `${apiUrl}/api/reportes-capacitacion`;

export class TrainingReportApiError extends Error {
  constructor(code, message, status = null) {
    super(message);
    this.name = 'TrainingReportApiError';
    this.code = code;
    this.status = status;
  }
}

const isBlob = (value) =>
  typeof Blob !== 'undefined' && value instanceof Blob;

async function readErrorPayload(error) {
  const data = error?.response?.data;
  if (!isBlob(data)) return data;
  if (typeof data.text !== 'function') return null;

  try {
    return JSON.parse(await data.text());
  } catch (_error) {
    return null;
  }
}

async function normalizeError(error) {
  const payload = await readErrorPayload(error);
  const backendError = payload?.error || payload;
  const code = backendError?.code || 'REQUEST_ERROR';
  const message =
    backendError?.message ||
    (error?.response
      ? 'No fue posible completar la operación del reporte.'
      : 'No fue posible comunicarse con el servicio de reportes.');
  const status = Number.isInteger(error?.response?.status)
    ? error.response.status
    : null;

  return new TrainingReportApiError(code, message, status);
}

function authConfig(accessToken, extra = {}) {
  return {
    ...extra,
    headers: {
      ...(extra.headers || {}),
      ...(accessToken
        ? { Authorization: `Bearer ${accessToken}` }
        : {}),
    },
  };
}

function unwrapJson(request) {
  return request
    .then(({ data }) => data?.data)
    .catch(async (error) => {
      throw await normalizeError(error);
    });
}

function unwrapBlob(request) {
  return request
    .then(({ data }) => data)
    .catch(async (error) => {
      throw await normalizeError(error);
    });
}

const reportUrl = (idReporte) =>
  `${baseUrl}/${encodeURIComponent(String(idReporte))}`;

export const trainingReportApi = {
  list: (accessToken) =>
    unwrapJson(axios.get(baseUrl, authConfig(accessToken))),

  create: (idClase, accessToken) =>
    unwrapJson(
      axios.post(baseUrl, { id_clase: idClase }, authConfig(accessToken)),
    ),

  get: (idReporte, accessToken) =>
    unwrapJson(axios.get(reportUrl(idReporte), authConfig(accessToken))),

  updateGeneralData: (idReporte, payload, accessToken) =>
    unwrapJson(
      axios.put(
        `${reportUrl(idReporte)}/datos-generales`,
        payload,
        authConfig(accessToken),
      ),
    ),

  generateAttendances: (idReporte, regenerate, accessToken) =>
    unwrapJson(
      axios.post(
        `${reportUrl(idReporte)}/generar-asistencias`,
        regenerate ? { regenerar: true } : {},
        authConfig(accessToken),
      ),
    ),

  updateParticipants: (idReporte, participantes, accessToken) =>
    unwrapJson(
      axios.put(
        `${reportUrl(idReporte)}/participantes`,
        { participantes },
        authConfig(accessToken),
      ),
    ),

  updateReconciledHours: (
    idReporte,
    idParticipante,
    horasConciliadasMinutos,
    accessToken,
  ) =>
    unwrapJson(
      axios.put(
        `${reportUrl(idReporte)}/participantes/${encodeURIComponent(
          String(idParticipante),
        )}/horas`,
        { horas_conciliadas_minutos: horasConciliadasMinutos },
        authConfig(accessToken),
      ),
    ),

  updateAttendances: (idReporte, asistencias, accessToken) =>
    unwrapJson(
      axios.put(
        `${reportUrl(idReporte)}/asistencias`,
        { asistencias },
        authConfig(accessToken),
      ),
    ),

  updateResults: (idReporte, resultados, accessToken) =>
    unwrapJson(
      axios.put(
        `${reportUrl(idReporte)}/resultados`,
        { resultados },
        authConfig(accessToken),
      ),
    ),

  summary: (idReporte, accessToken) =>
    unwrapJson(
      axios.get(`${reportUrl(idReporte)}/resumen`, authConfig(accessToken)),
    ),

  uploadEvidence: (idReporte, files, metadata, accessToken) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('archivos', file));
    if (metadata?.descripcion) {
      formData.append('descripcion', metadata.descripcion);
    }
    if (metadata?.fecha_evidencia) {
      formData.append('fecha_evidencia', metadata.fecha_evidencia);
    }

    return unwrapJson(
      axios.post(
        `${reportUrl(idReporte)}/evidencias`,
        formData,
        authConfig(accessToken),
      ),
    );
  },

  updateEvidence: (idReporte, idEvidencia, payload, accessToken) =>
    unwrapJson(
      axios.put(
        `${reportUrl(idReporte)}/evidencias/${encodeURIComponent(
          String(idEvidencia),
        )}`,
        payload,
        authConfig(accessToken),
      ),
    ),

  deleteEvidence: (idReporte, idEvidencia, accessToken) =>
    unwrapJson(
      axios.delete(
        `${reportUrl(idReporte)}/evidencias/${encodeURIComponent(
          String(idEvidencia),
        )}`,
        authConfig(accessToken),
      ),
    ),

  reorderEvidence: (idReporte, orden, accessToken) =>
    unwrapJson(
      axios.put(
        `${reportUrl(idReporte)}/evidencias/orden`,
        { orden },
        authConfig(accessToken),
      ),
    ),

  evidenceFile: (idReporte, idEvidencia, accessToken) =>
    unwrapBlob(
      axios.get(
        `${reportUrl(idReporte)}/evidencias/${encodeURIComponent(
          String(idEvidencia),
        )}/archivo`,
        authConfig(accessToken, { responseType: 'blob' }),
      ),
    ),

  preview: (idReporte, accessToken) =>
    unwrapBlob(
      axios.get(
        `${reportUrl(idReporte)}/preview`,
        authConfig(accessToken, { responseType: 'blob' }),
      ),
    ),

  generate: (idReporte, accessToken) =>
    unwrapJson(
      axios.post(
        `${reportUrl(idReporte)}/generar`,
        {},
        authConfig(accessToken),
      ),
    ),

  download: (idReporte, accessToken) =>
    unwrapBlob(
      axios.get(
        `${reportUrl(idReporte)}/descargar`,
        authConfig(accessToken, { responseType: 'blob' }),
      ),
    ),
};
